import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Card, Input, Select, Table, EmptyState,
} from '../../../design-system';
import { money, day } from '../../../lib/tallyBooks';
import '../../styles/TallyPanel.css';

// The client's chart of accounts, as Tally holds it.
//
// ⚠️ THIS IS THE VIEW THE WHOLE SYNC EXISTS FOR, AND IT DID NOT EXIST. Ledgers
// were pulled every night, ingested into TallyLedger and served by
// `GET /api/tally/connections/:id/ledgers` — and nothing anywhere rendered
// them. The only window onto a client's books was the party-matching screen,
// which is a screen about the FIRM's client list that happens to read ledgers.
// So the tab looked like it was trying to turn the client's suppliers into
// clients, because that was the only thing it did with them.
//
// ⚠️ READ-ONLY, STRUCTURALLY. There is no write path to a ledger and there is
// deliberately no useful one — see the header of utils/tally/requests.js. Tally
// owns these rows; we hold a copy of known age.

function TallyLedgers({ connectionId, onOpenLedger }) {
  const [ledgers, setLedgers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [parent, setParent] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/tally/connections/${connectionId}/ledgers`, {
        params: { q: q.trim() || undefined, parent: parent || undefined, limit: 500 },
      });
      setLedgers(res.data?.ledgers || []);
      setGroups(res.data?.groups || []);
      setTotal(res.data?.total || 0);
    } catch {
      // Leave the last known rows rather than blanking the table — a transient
      // 401 during a token refresh must not read as "these books are empty".
    } finally {
      setLoading(false);
    }
  }, [connectionId, q, parent]);

  // ⚠️ DEBOUNCED ON THE SEARCH BOX, because the filter is server-side: typing
  // "sundry" unthrottled is six round trips over a few hundred ledgers, and the
  // answers can land out of order so the list settles on whichever was slowest.
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <Card>
      <div className="tally-head">
        <div>
          <h3 className="tally-section-title">Ledgers</h3>
          <p className="tally-muted">
            This client&apos;s chart of accounts, exactly as Tally reports it —
            balances included. Nothing here is editable: Tally owns these, and we
            hold a copy of known age.
          </p>
        </div>
      </div>

      <div className="tally-filters">
        <Input
          label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ledger name…"
        />
        <Select label="Group" value={parent} onChange={(e) => setParent(e.target.value)}>
          <option value="">All groups</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </Select>
      </div>

      {loading && !ledgers.length ? (
        <p className="tally-muted">Loading…</p>
      ) : !ledgers.length ? (
        <EmptyState
          icon="📒"
          title={q || parent ? 'No ledger matches that' : 'No ledgers mirrored yet'}
          description={
            q || parent
              ? 'Try a different name or group.'
              : 'They arrive on the first successful sync. If the last attempt failed, the reason is at the top of this tab.'
          }
        />
      ) : (
        <>
          <Table
            columns={[
              {
                key: 'name',
                header: 'Ledger',
                // The name is the way in. An opening and a closing balance beg
                // the question of what moved between them, and until this the
                // table could not answer it.
                //
                // ⚠️ THE PARENT OWNS THE DRILL-DOWN, not this list. It opens a
                // whole screen and has to save this page's scroll position on
                // the way in — neither of which a table cell can do.
                render: (l) => (
                  <button
                    type="button"
                    className="tally-linkish tally-linkish-inline"
                    onClick={() => onOpenLedger(l._id)}
                  >
                    {l.name}
                  </button>
                ),
              },
              { key: 'parent', header: 'Group', render: (l) => l.parent || '—' },
              { key: 'gstin', header: 'GSTIN', render: (l) => l.gstin || '—' },
              { key: 'pan', header: 'PAN', render: (l) => l.pan || '—' },
              {
                key: 'opening',
                header: 'Opening',
                render: (l) => <span className="tally-num">{money(l.openingBalance)}</span>,
              },
              {
                key: 'closing',
                header: 'Closing',
                render: (l) => <span className="tally-num">{money(l.closingBalance)}</span>,
              },
              { key: 'synced', header: 'Read', render: (l) => day(l.syncedAt) },
            ]}
            data={ledgers}
            keyField="_id"
          />
          {/* ⚠️ SAID OUT LOUD WHEN THE LIST IS CUT SHORT. A table that silently
              stops at 500 of 900 rows is a table somebody reads a total off. */}
          {total > ledgers.length && (
            <p className="tally-muted">
              {`Showing ${ledgers.length} of ${total}. Narrow it with the search or the group filter.`}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

export default TallyLedgers;
